/**
 * ChatContext - State management for chat
 * 
 * Handles:
 * - WebSocket connection lifecycle (connect after auth, disconnect on logout)
 * - Real-time message updates
 * - Chat list management
 * - Current chat state
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AuthContext } from '../components/AuthContext';
import * as chatService from '../services/chatService';

const ChatContext = createContext();

// WebSocket URL - use wss for production, ws for localhost
const getWebSocketUrl = (token) => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/ws/chat/?token=${token}`;
};

export const ChatProvider = ({ children }) => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [sendingMessages, setSendingMessages] = useState(new Map()); // Map of tempId -> message data
  const tempIdCounterRef = useRef(0);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const currentChatRef = useRef(null);
  
  // Keep currentChat ref in sync
  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!isAuthenticated || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const token = localStorage.getItem('access');
    if (!token) {
      return;
    }

    try {
      const wsUrl = getWebSocketUrl(token);
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('Chat WebSocket connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('Chat WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('Chat WebSocket disconnected');
        setIsConnected(false);
        
        // Attempt to reconnect
        if (isAuthenticated && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Error connecting to Chat WebSocket:', error);
    }
  }, [isAuthenticated]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'chat_message':
        // New message received
        const { message: receivedMessage, chat_id } = data;
        
        // If this is our own message (checking by comparing sender with current user)
        // and we have a sending message with matching content, replace optimistic message
        const currentUserId = user?.id;
        const isOwnMessage = receivedMessage.sender?.id === currentUserId;
        
        // Update messages if viewing this chat (use ref to avoid stale closure)
        setMessages(prev => {
          const currentChatId = currentChatRef.current?.id;
          if (currentChatId === chat_id) {
            // If it's our own message, try to find and replace optimistic message
            if (isOwnMessage) {
              // Find optimistic message with matching content
              const optimisticIndex = prev.findIndex(
                m => m.temp_id && m.content === receivedMessage.content
              );
              
              if (optimisticIndex >= 0) {
                // Replace optimistic message with real one
                const updated = [...prev];
                updated[optimisticIndex] = { ...receivedMessage, is_sending: false };
                return updated;
              }
            }
            
            // Check if message already exists (to prevent duplicates)
            const exists = prev.find(m => m.id === receivedMessage.id);
            if (!exists) {
              return [...prev, { ...receivedMessage, is_sending: false }];
            }
          }
          return prev;
        });
        
        // Update sending messages - remove if it's our message
        if (isOwnMessage) {
          setSendingMessages(prev => {
            const newMap = new Map(prev);
            // Find and remove matching sending message
            for (const [tempId, sendingData] of newMap.entries()) {
              if (sendingData.chatId === chat_id && sendingData.content === receivedMessage.content) {
                newMap.delete(tempId);
                break;
              }
            }
            return newMap;
          });
        }
        
        // Update chat list (bump to top, update last message)
        setChats(prev => {
          const chatIndex = prev.findIndex(c => c.id === chat_id);
          if (chatIndex >= 0) {
            const updatedChats = [...prev];
            const chat = updatedChats[chatIndex];
            const currentChatId = currentChatRef.current?.id;
            updatedChats[chatIndex] = {
              ...chat,
              last_message: {
                id: receivedMessage.id,
                content: receivedMessage.content,
                sender_id: receivedMessage.sender.id,
                created_at: receivedMessage.created_at,
              },
              updated_at: receivedMessage.created_at,
              unread_count: chat.id === currentChatId ? 0 : (chat.unread_count || 0) + 1,
            };
            // Move to top
            updatedChats.unshift(updatedChats.splice(chatIndex, 1)[0]);
            return updatedChats;
          }
          // If chat not in list, refresh chats
          fetchChatsRef.current();
          return prev;
        });
        break;
        
      case 'typing':
        // Handle typing indicator (future feature)
        break;
        
      case 'stop_typing':
        // Handle stop typing (future feature)
        break;
        
      case 'error':
        console.error('Chat WebSocket error:', data.message);
        break;
        
      default:
        console.warn('Unknown Chat WebSocket message type:', data.type);
    }
  }, [user]); // Include user to check for own messages

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Fetch chats
  const fetchChats = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await chatService.getChats();
      // Handle both paginated response (with results) and direct array
      const chatsList = Array.isArray(data) ? data : (data.results || []);
      setChats(chatsList);
    } catch (error) {
      console.error('Failed to fetch chats:', error);
      // Set empty array on error to prevent white screen
      setChats([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch chats ref for use in WebSocket handler
  const fetchChatsRef = useRef(fetchChats);
  useEffect(() => {
    fetchChatsRef.current = fetchChats;
  }, [fetchChats]);

  // Fetch chat details
  const fetchChat = useCallback(async (chatId) => {
    setIsLoading(true);
    try {
      const chat = await chatService.getChat(chatId);
      setCurrentChat(chat);
      setMessages(chat.messages || []);
      return chat;
    } catch (error) {
      console.error('Failed to fetch chat:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get or create chat with user
  const getChatWithUser = useCallback(async (userId) => {
    setIsLoading(true);
    try {
      const chat = await chatService.getChatWithUser(userId);
      
      // Update chats list
      setChats(prev => {
        const existingIndex = prev.findIndex(c => c.id === chat.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = chat;
          return updated;
        }
        return [chat, ...prev];
      });
      
      setCurrentChat(chat);
      setMessages(chat.messages || []);
      return chat;
    } catch (error) {
      console.error('Failed to get/create chat with user:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send message with Telegram-style optimistic UI
  const sendMessage = useCallback(async (chatId, content) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const currentChatId = currentChatRef.current?.id;
    const currentUser = user;
    
    // Generate temporary ID for optimistic message
    const tempId = `temp_${Date.now()}_${tempIdCounterRef.current++}`;
    const optimisticMessage = {
      id: tempId,
      temp_id: tempId,
      sender: currentUser,
      content: content,
      created_at: new Date().toISOString(),
      is_sending: true, // Flag to show clock icon
      is_read: false,
    };
    
    // Add optimistic message immediately (only if viewing this chat)
    if (currentChatId === chatId) {
      setMessages(prev => [...prev, optimisticMessage]);
    }
    
    // Track sending message
    setSendingMessages(prev => {
      const newMap = new Map(prev);
      newMap.set(tempId, { chatId, content, timestamp: Date.now() });
      return newMap;
    });
    
    // Update chat's last message optimistically
    setChats(prev => {
      const chatIndex = prev.findIndex(c => c.id === chatId);
      if (chatIndex >= 0) {
        const updated = [...prev];
        updated[chatIndex] = {
          ...updated[chatIndex],
          last_message: {
            id: tempId,
            content: content,
            sender_id: currentUser?.id,
            created_at: optimisticMessage.created_at,
          },
          updated_at: optimisticMessage.created_at,
        };
        // Move to top
        updated.unshift(updated.splice(chatIndex, 1)[0]);
        return updated;
      }
      return prev;
    });
    
    try {
      // Send message to server
      const message = await chatService.sendMessage(chatId, content);
      
      // Remove from sending messages
      setSendingMessages(prev => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        return newMap;
      });
      
      // Replace optimistic message with real message (only if viewing this chat)
      if (currentChatId === chatId) {
        setMessages(prev => {
          // Find optimistic message to replace
          const optimisticIndex = prev.findIndex(m => m.temp_id === tempId);
          
          if (optimisticIndex >= 0) {
            // Replace optimistic message with real one
            const updated = [...prev];
            updated[optimisticIndex] = { ...message, is_sending: false };
            return updated;
          } else {
            // Optimistic message not found (maybe already replaced by WebSocket)
            // Check if message already exists
            const exists = prev.find(m => m.id === message.id);
            if (!exists) {
              return [...prev, { ...message, is_sending: false }];
            }
            // Update existing message
            return prev.map(m => m.id === message.id ? { ...m, is_sending: false } : m);
          }
        });
      }
      
      // Update chat's last message with real message
      setChats(prev => {
        const chatIndex = prev.findIndex(c => c.id === chatId);
        if (chatIndex >= 0) {
          const updated = [...prev];
          updated[chatIndex] = {
            ...updated[chatIndex],
            last_message: {
              id: message.id,
              content: message.content,
              sender_id: message.sender.id,
              created_at: message.created_at,
            },
            updated_at: message.created_at,
          };
          // Move to top
          updated.unshift(updated.splice(chatIndex, 1)[0]);
          return updated;
        }
        return prev;
      });
      
      return message;
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Remove optimistic message on error
      setSendingMessages(prev => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        return newMap;
      });
      
      if (currentChatId === chatId) {
        setMessages(prev => prev.filter(m => m.temp_id !== tempId));
      }
      
      // On error, just revert the optimistic update
      // The chat list will be updated when we remove the optimistic message
      
      throw error;
    }
  }, [user]);

  // Mark messages as read
  const markMessagesRead = useCallback(async (chatId, messageIds = null) => {
    try {
      await chatService.markMessagesRead(chatId, messageIds);
      
      // Update local state
      if (currentChat && currentChat.id === chatId) {
        setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      }
      
      setChats(prev => prev.map(c => 
        c.id === chatId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  }, [currentChat]);

  // Connect/disconnect WebSocket based on auth status
  useEffect(() => {
    if (isAuthenticated) {
      const startupTimer = setTimeout(() => {
        connectWebSocket();
      }, 2000);
      return () => {
        clearTimeout(startupTimer);
        disconnectWebSocket();
      };
    } else {
      disconnectWebSocket();
      setChats([]);
      setCurrentChat(null);
      setMessages([]);
    }
  }, [isAuthenticated, connectWebSocket, disconnectWebSocket]);

  const value = {
    chats,
    currentChat,
    messages,
    isLoading,
    isConnected,
    fetchChats,
    fetchChat,
    getChatWithUser,
    sendMessage,
    markMessagesRead,
    setCurrentChat,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};